import * as Sentry from "@sentry/react";

import {
  getCollectionOfDittoComponents,
  useTexts,
} from "Components/TextContext";
import {
  useCoveragePriceCalculations,
  useFormState,
} from "Store/priceCalculation/priceCalculationHooks";
import { Form, FormikProps } from "formik";

import { AkaRenewalInfoAlert } from "Components/AkaRenewalInfoAlert";
import { CurrentPriceMessage } from "Components/CurrentPriceMessage/CurrentPriceMessage";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { ProductPriceTile } from "Components/Features/CheckPrice/Tiles/ProductPriceTile";
import { DittoCoverageCard } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/DittoCoverages/DittoCoverageCard";
import { StyledComparisonWrap } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/productPriceTile.styles";
import { BackendErrorAlert } from "Components/Form/CommonFormElements/BackendErrorAlert/BackendErrorAlert";
import { InputFailureTracker } from "Components/InputFailureTracker";
import { NavigationButtons } from "Components/NavigationButtons";
import { TextComponentNameLevel1 } from "Components/TextContext/textConstants";
import { useAddProductToCartHandler } from "Store/addProductToCart/hooks";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { ReduxState } from "Store/rootReducer";
import React from "react";
import { useSelector } from "react-redux";
import { AKA_HOUSE_CONTENTS_PRICE_STEP } from "..";
import { AKA_HOUSE_CONTENTS_DEDUCTIBLE } from "../akaHouseContentsConstants";
import { AkaHouseContentsProductPriceFormValues } from "../akaHouseContentsFormValuesInterfaces";
import { validationSchema } from "./akaHouseContentsProductPriceValidation";

export const AkaHouseContentsProductPrice = (): React.ReactElement => {
  const { getText } = useTexts();
  const handleAddProductToCart = useAddProductToCartHandler(
    AKA_HOUSE_CONTENTS_PRICE_STEP.id,
  );
  const { priceCalculation, addProductToCart } = useSelector(
    (state: ReduxState) => state,
  );

  const { akahousecontents: productCardTexts } = getCollectionOfDittoComponents(
    TextComponentNameLevel1.COVERAGE_CARD,
  );

  const [coveragePriceCalculation] = useCoveragePriceCalculations();

  const initialFormState = useFormState<AkaHouseContentsProductPriceFormValues>(
    AKA_HOUSE_CONTENTS_PRICE_STEP.id,
    {
      coverage: coveragePriceCalculation[0],
    },
  );

  const handleSubmit = (
    formProps: AkaHouseContentsProductPriceFormValues,
  ): void => {
    const productToAdd = {
      coverage: formProps.coverage,
      productType: INSURANCE_NAME_VALUE.AKA_HOUSE_CONTENTS,
      filterValue: AKA_HOUSE_CONTENTS_DEDUCTIBLE,
    };
    handleAddProductToCart(formProps, [productToAdd]);
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductPrice - ${INSURANCE_NAME_VALUE.AKA_HOUSE_CONTENTS}`,
        );
      }}
    >
      <ProductPriceTile
        validationSchema={validationSchema()}
        initialValues={initialFormState}
        onSubmit={handleSubmit}
        header={getText("pageheader.akahousecontents.checkprice")}
      >
        {(formProps: FormikProps<AkaHouseContentsProductPriceFormValues>) => {
          return (
            <Form>
              <AkaRenewalInfoAlert
                productName={INSURANCE_NAME_VALUE.AKA_HOUSE_CONTENTS}
              />
              <DittoCoverageCard
                fieldName="coverage"
                product={ProductBackendName.AKA_HOUSE_CONTENTS}
                coverage={coveragePriceCalculation[0]}
                textComponents={productCardTexts}
                prices={coveragePriceCalculation[1].prices}
                priceIsLoading={
                  priceCalculation.priceCalculationResult.isLoading
                }
                hideRadioButton
              />
              <CurrentPriceMessage
                productBackendName={ProductBackendName.AKA_HOUSE_CONTENTS}
                coverages={[coveragePriceCalculation]}
                selectedPrices={[
                  {
                    selectedCoverage: formProps.values.coverage,
                    selectedDeductible: undefined,
                    defaultDeductible: undefined,
                  },
                ]}
              />
              <BackendErrorAlert
                priceCalculation={priceCalculation.priceCalculationResult}
                addProductToCart={addProductToCart}
              />
              <StyledComparisonWrap>
                <NavigationButtons isLoading={addProductToCart.isLoading} />
              </StyledComparisonWrap>
              <InputFailureTracker
                productName={INSURANCE_NAME_VALUE.AKA_HOUSE_CONTENTS}
                stepId={AKA_HOUSE_CONTENTS_PRICE_STEP.id}
              />
            </Form>
          );
        }}
      </ProductPriceTile>
    </Sentry.ErrorBoundary>
  );
};
